import React from 'react';
import {
  ReactFlow, 
  Background, 
  Controls,
  MiniMap,
  Node,
  Edge,
  BackgroundVariant
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { ReleaseCustomNode } from './ReleaseCustomNode';
import type { 
  ReleaseFlowDto,
  ReleaseFlowNodeDto,
  ReleaseFlowEdgeDto 
} from '../../types/releaseFlow';
import type { 
  ReleaseNodePipelineRunDto 
} from '../../types/release';

const nodeTypes: any = {
  custom: ReleaseCustomNode,
};

interface ReleaseFlowViewerProps {
  releaseFlow: ReleaseFlowDto;
  releaseId: string;
  releaseNodePipelineRuns?: ReleaseNodePipelineRunDto[];
  className?: string;
}

// Convert backend nodes to ReactFlow nodes
const convertToReactFlowNodes = (
  nodes: ReleaseFlowNodeDto[], 
  releaseId: string,
  releaseNodePipelineRuns?: ReleaseNodePipelineRunDto[]
): Node[] => {
  return nodes.map((node) => {
    // Convert single nodePipelineLink to pipelineAssociations array format
    const pipelineAssociations = node.nodePipelineLink ? [node.nodePipelineLink] : [];
    
    return {
      id: node.id,
      type: 'custom',
      position: { x: node.positionX, y: node.positionY },
      data: {
        label: node.name,
        nodeType: node.nodeType as any,
        description: node.description,
        isActive: node.isActive,
        backgroundColor: node.backgroundColor,
        borderColor: node.borderColor,
        width: node.width,
        height: node.height,
        pipelineAssociations: pipelineAssociations,
        releaseId: releaseId,
        nodeId: node.id,
        releaseNodePipelineRuns: releaseNodePipelineRuns,
        readOnly: false, // Allow clicking to select pipeline runs
      },
    };
  });
};

// Convert backend edges to ReactFlow edges
const convertToReactFlowEdges = (edges: ReleaseFlowEdgeDto[]): Edge[] => {
  return edges.map((edge) => {
    let style;
    try {
      style = edge.style ? JSON.parse(edge.style) : undefined;
    } catch {
      style = undefined;
    }

    return {
      id: edge.id,
      source: edge.sourceNodeId,
      target: edge.targetNodeId,
      type: 'smoothstep',
      label: edge.label,
      animated: edge.animated,
      style,
      markerEnd: edge.markerEnd ? { type: edge.markerEnd as 'arrowclosed' } : { type: 'arrowclosed' },
    };
  });
};

export const ReleaseFlowViewer: React.FC<ReleaseFlowViewerProps> = ({
  releaseFlow,
  releaseId,
  releaseNodePipelineRuns = [],
  className = ''
}) => {
  const nodes = convertToReactFlowNodes(releaseFlow.nodes, releaseId, releaseNodePipelineRuns);
  const edges = convertToReactFlowEdges(releaseFlow.edges);

  return (
    <div className={`w-full h-full ${className}`}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        attributionPosition="bottom-left"
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={true}
        panOnDrag={true}
        zoomOnScroll={true}
        zoomOnPinch={true}
        zoomOnDoubleClick={false}
        selectNodesOnDrag={false}
        minZoom={0.1}
        maxZoom={2}
      >
        <Background 
          variant={BackgroundVariant.Dots} 
          gap={20} 
          size={1}
          color="#e5e7eb"
        />
        <Controls 
          showZoom={true}
          showFitView={true}
          showInteractive={false}
          position="top-left"
        />
        <MiniMap 
          position="bottom-right"
          nodeColor="#e5e7eb"
          nodeBorderRadius={4}
        />
      </ReactFlow>
    </div>
  );
};